import React from 'react';
import { Brain, Star, Users, TrendingUp, Building, Target, GraduationCap, Cloud, Code, Bot, Sparkles, Eye, Mic, Database, Lock, Scale, Heart, Globe, Shield, CheckCircle, Zap, Settings, BarChart3, Award, Rocket, Blocks } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const AboutPage: React.FC = () => {
  useSEO('about');
  const { isBannerVisible, bannerHeight } = useBanner();
  
  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6 py-4 px-2 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent leading-loose overflow-visible min-h-fit"
            style={{ lineHeight: '1.75', minHeight: 'auto', overflow: 'visible' }}
          >
            About Us
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-7xl mx-auto leading-relaxed">
            We are a comprehensive AI and blockchain solutions company with locations in East Lansing, Michigan and Houston, Texas, 
            offering specialized AI services, enterprise blockchain technology, and developing innovative healthcare solutions to empower businesses 
            across all industries to thrive in the digital transformation era.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Our Mission</h2>
                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
                  To democratize AI technology and make cutting-edge digital solutions 
                  accessible to businesses of all sizes. We believe that every company 
                  deserves the opportunity to harness the power of artificial intelligence 
                  to drive growth, efficiency, and innovation.
                </p>
                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
                  Through our comprehensive suite of specialized AI services, custom software development, cloud infrastructure,
                  and enterprise blockchain solutions, we help organizations transform their operations,
                  enhance customer experiences, and stay competitive in an increasingly digital world.
                </p>
              </div>
              <div className="flex justify-center">
                <div className="w-64 h-64 bg-gradient-to-r from-blue-600/20 to-blue-700/20 rounded-full flex items-center justify-center">
                  <Brain className="w-32 h-32 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>
          </section>

          <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Expert Team</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Skilled professionals with years of experience in AI and digital solutions
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Proven Results</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Track record of successful implementations and satisfied clients
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Innovation</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Cutting-edge technologies and innovative approaches to problem-solving
              </p>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                <Building className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Multi-Location Presence</h3>
              <p className="text-gray-700 dark:text-gray-300">
                Based in East Lansing, Michigan and Houston, Texas, serving clients nationwide
              </p>
            </div>
          </section>

          {/* Services Overview */}
          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Comprehensive AI & Blockchain Services</h2>
            <p className="text-lg text-gray-700 dark:text-gray-300 text-center mb-8 max-w-4xl mx-auto">
              We offer specialized AI and blockchain services designed to address every aspect of your digital transformation journey, 
              from initial strategy to full implementation and compliance.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Target className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Reach</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Digital Marketing</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <GraduationCap className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Train</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">AI Training & Consulting</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Cloud className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Cloud</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Infrastructure</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Code className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Build</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Custom Software</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Brain className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Intelligence</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">AI & Data Analytics</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Bot className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Agents</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">AI Automation & RPA</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Generate</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Generative AI</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Eye className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Vision</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Computer Vision</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Mic className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Voice</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Conversational AI</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Predict</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Advanced Analytics</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Database className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive IoT</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Internet of Things</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Secure</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Cybersecurity</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Scale className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Compliance</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Regulatory AI</p>
              </div>
              <div className="flex flex-col items-center p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10">
                <Blocks className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" />
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white text-center">Cognitive Ledger</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Blockchain Technology</p>
              </div>
            </div>
          </section>

          {/* Why Choose Us Section */}
          <section className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8 mb-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Why Choose Cognitive Craft Hub</h2>
              <div className="bg-white/60 dark:bg-white/10 rounded-2xl p-6 max-w-4xl mx-auto">
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Our Core Promise</h3>
                <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                  "From concept to implementation — we craft intelligent, secure, and scalable software solutions 
                  designed to drive meaningful business results across diverse industries."
                </p>
              </div>
            </div>

            {/* Cognitive Blueprint Framework */}
            <div className="mb-12">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Signature Framework – Cognitive Blueprint™</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">Insight Mapping</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-center text-sm">
                    Comprehensive analysis of business processes and operational bottlenecks
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">Solution Architecture</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-center text-sm">
                    Technical solutions designed with scalability, security, and compliance standards
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">Rapid Prototyping</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-center text-sm">
                    Fast development of functional prototypes for concept validation
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <Code className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">Precision Development</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-center text-sm">
                    Development following industry best practices with strategic AI integration
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3 text-center">Continuous Optimization</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-center text-sm">
                    Post-launch improvements guided by real-world performance data
                  </p>
                </div>
              </div>
            </div>

            {/* Unique Accelerators */}
            <div className="mb-12">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Pre-Built Technology Accelerators</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Shield className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Crafted Core™</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Secure, multi-tenant architecture designed with HIPAA, SOC 2, and GDPR compliance frameworks
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Bot className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">FlowFusion™</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    AI-enhanced workflow automation engine with ERP and CRM integration capabilities
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Lock className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">SecureConnect™</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Advanced authentication and API gateway for role-based, multi-tenant access management
                  </p>
                </div>
              </div>
            </div>

            {/* Industry Applications */}
            <div className="mb-12">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Cross-Industry Excellence</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Heart className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Healthcare</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Streamlined patient scheduling with AI-powered optimization
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Logistics</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Automated shipment tracking across multiple carrier platforms
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <BarChart3 className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Finance</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Enhanced compliance reporting with real-time data validation
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                  <h4 className="font-bold text-gray-900 dark:text-white mb-2">Retail</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Personalized customer experiences through AI-driven analytics
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Experience Differentiators */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Client Experience Excellence</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Award className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Impact Reporting</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Regular business metrics reporting focused on measurable outcomes, not just technical updates
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Globe className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Client Portal Access</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Centralized project tracking, documentation, and communication platform
                  </p>
                </div>
                <div className="bg-white/60 dark:bg-white/10 rounded-xl p-6 text-center">
                  <Rocket className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Launch & Optimize Program</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Comprehensive 90-day post-launch performance optimization and support
                  </p>
                </div>
              </div>
            </div>

            <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-8 leading-relaxed">
              *Cognitive Blueprint™, Crafted Core™, FlowFusion™, and SecureConnect™ are proprietary methodologies and 
              technology frameworks developed by Cognitive Craft Hub. Results and performance improvements may vary 
              based on project scope, industry requirements, and implementation factors.
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Our Values</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Excellence</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  We strive for excellence in every project, delivering high-quality 
                  solutions that exceed expectations and drive real business value.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Innovation</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  We embrace emerging technologies and innovative approaches to stay 
                  ahead of the curve and provide cutting-edge solutions.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Partnership</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  We work closely with our clients as trusted partners, understanding 
                  their unique needs and crafting tailored solutions.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};